import { ArrowRight } from "lucide-react";
import {
  articlePath,
  listCategories,
  listIndexableArticles,
  readingTimeMinutes,
} from "../content";
import {
  DEFAULT_LOCALE,
  LOCALE_TAGS,
  localePath,
  useLocale,
  useMessages,
} from "../i18n";
import { articleIndexCrumbs } from "../seo";
import { ARTICLES_PATH, FAQ_PATH } from "../shared";
import { Breadcrumbs, ReadingShell } from "./ReadingShell";

/**
 * The catalogue.
 *
 * It lists `listIndexableArticles` rather than the raw registry, so an article
 * hidden from discovery cannot appear here and then be missing from the
 * sitemap. Grouping is by editorial category, which is a public fact about the
 * article rather than anything about the reader.
 *
 * The page around the cards is translated; the guides themselves are not. Each
 * card is therefore marked `lang` and `hreflang` English, which is what tells a
 * browser to read the title in an English voice on a Thai page, and tells a
 * crawler that the link leaves the language it is currently in. The line under
 * the heading says the same thing to the reader in words.
 */
export function ArticleIndexView() {
  const locale = useLocale();
  const t = useMessages();
  const articles = listIndexableArticles();
  const categories = listCategories();
  const foreign = locale !== DEFAULT_LOCALE;

  return (
    <ReadingShell path={ARTICLES_PATH}>
      <main className="index-page">
        <Breadcrumbs crumbs={articleIndexCrumbs(locale)} />

        <header className="index-page__head">
          <p className="tag tag--sun">{t.articles.tag}</p>
          <h1>{t.articles.title}</h1>
          <p className="index-page__lede">{t.seo.articlesDescription}</p>
          {foreign ? (
            <p className="index-page__note">{t.home.readsInEnglish}</p>
          ) : null}
        </header>

        {categories.map((category) => (
          <section className="index-page__group" key={category}>
            <h2>{t.articles.categories[category] ?? category}</h2>
            <ul className="card-grid">
              {articles
                .filter((article) => article.category === category)
                .map((article) => (
                  <li key={article.slug}>
                    <a
                      className="card"
                      href={articlePath(article.slug)}
                      hrefLang={foreign ? LOCALE_TAGS[DEFAULT_LOCALE] : undefined}
                    >
                      <span className="card__emoji" aria-hidden="true">
                        {article.emoji}
                      </span>
                      <h3 lang={foreign ? LOCALE_TAGS[DEFAULT_LOCALE] : undefined}>
                        {article.title}
                      </h3>
                      <p lang={foreign ? LOCALE_TAGS[DEFAULT_LOCALE] : undefined}>
                        {article.description}
                      </p>
                      <span className="card__meta">
                        {t.articles.minRead(readingTimeMinutes(article))}
                      </span>
                    </a>
                  </li>
                ))}
            </ul>
          </section>
        ))}

        <section className="index-page__cta">
          <h2>{t.articles.ctaTitle}</h2>
          <p>
            {t.articles.ctaBody}{" "}
            <a href={localePath(locale, FAQ_PATH)}>{t.articles.ctaLink}</a>.
          </p>
          <a
            className="btn btn--primary btn--lg"
            href={localePath(locale, "/")}
          >
            <span className="btn__icon" aria-hidden="true">
              <ArrowRight size={19} />
            </span>
            <span>{t.nav.startCourse}</span>
          </a>
        </section>
      </main>
    </ReadingShell>
  );
}
